import CONFIG from '../config'
import {
    HANDSHAKE_ERRORS,
    HANDSHAKE_TIMEOUT_MS,
    PLAYER_READY,
    PLAYER_READY_RETRY_MS,
    SEND_PLAYER_TOKEN,
} from '../constants/handshake'

/**
 * The window that opened us - the CMS tab for a popup preview, the parent
 * frame when the player is embedded. `window.parent` is `window` itself on a
 * top level page, which is not a valid handshake peer.
 */
function getCmsWindow() {
    if (window.opener && !window.opener.closed) return window.opener
    if (window.parent && window.parent !== window) return window.parent
    return null
}

function getAllowedOrigins() {
    return CONFIG.cmsOrigins || []
}

/**
 * True when this page load is a preview that expects the token over
 * postMessage: a test_id in the URL and no token alongside it.
 */
export function isHandshakePreview() {
    const params = new URLSearchParams(window.location.search)
    return Boolean(params.get('test_id') && !params.get('tp_preview_token'))
}

/**
 * Removes tp_preview_token from the address bar on the legacy token-in-URL
 * flow so it doesn't survive in history, bookmarks or the referrer header.
 */
export function scrubTokenFromUrl() {
    const params = new URLSearchParams(window.location.search)
    if (!params.get('tp_preview_token')) return

    params.delete('tp_preview_token')
    const query = params.toString()
    window.history.replaceState(
        window.history.state,
        '',
        `${window.location.pathname}${query ? `?${query}` : ''}${
            window.location.hash
        }`,
    )
}

/**
 * Announces PLAYER_READY to the CMS and resolves with the tp_preview_token it
 * sends back. The token only ever lives in memory here - the caller decides
 * where it goes next.
 *
 * Rejects with a HANDSHAKE_ERRORS code when there is no CMS window to talk to,
 * no configured origin to trust, or the token never arrives.
 */
export function requestPreviewToken({ timeoutMs = HANDSHAKE_TIMEOUT_MS } = {}) {
    return new Promise((resolve, reject) => {
        const cmsWindow = getCmsWindow()
        const allowedOrigins = getAllowedOrigins()

        if (!cmsWindow) {
            reject(new Error(HANDSHAKE_ERRORS.NO_OPENER))
            return
        }

        if (!allowedOrigins.length) {
            reject(new Error(HANDSHAKE_ERRORS.NOT_CONFIGURED))
            return
        }

        let readyInterval = null
        let timeoutId = null
        let announced = false

        function cleanup() {
            window.removeEventListener('message', onMessage)
            clearInterval(readyInterval)
            clearTimeout(timeoutId)
        }

        function onMessage(event) {
            if (event.data?.type !== SEND_PLAYER_TOKEN) return

            console.warn('[Handshake] Token message from:', event.origin)

            // Only a configured CMS origin, and only the window that opened
            // us, may hand over a token.
            if (!allowedOrigins.includes(event.origin)) {
                console.warn('[Handshake] Rejected untrusted origin:', event.origin)
                return
            }
            if (event.source !== cmsWindow) {
                console.warn('[Handshake] Rejected message from another window')
                return
            }

            const token = event.data.token
            if (typeof token !== 'string' || !token) {
                cleanup()
                reject(new Error(HANDSHAKE_ERRORS.TIMEOUT))
                return
            }

            cleanup()
            resolve(token)
        }

        function announceReady() {
            allowedOrigins.forEach((origin) => {
                if (!announced) console.warn('[Handshake] PLAYER_READY ->', origin)
                try {
                    cmsWindow.postMessage(PLAYER_READY, origin)
                } catch (error) {
                    console.error('handshake - PLAYER_READY failed', error)
                }
            })
        }

        window.addEventListener('message', onMessage)
        announceReady()
        announced = true
        readyInterval = setInterval(announceReady, PLAYER_READY_RETRY_MS)
        timeoutId = setTimeout(() => {
            cleanup()
            reject(new Error(HANDSHAKE_ERRORS.TIMEOUT))
        }, timeoutMs)
    })
}
